import { useCallback, useEffect, useRef, useState } from "react";
import { BellOff, ChevronLeft, ChevronRight, Loader2, Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { VisitRecord } from "./VisitRecord";
import { STATUS_WORDS, TONE_CLS, clock, dayHeading, pretty } from "./util";

const PERIODS = [[7, "This week"], [30, "Last 30 days"], [90, "Last 90 days"], [365, "Last 12 months"]];
const PER_PAGE = [25, 50, 100];
const sel = "w-full rounded-xl border border-sand-200 bg-white px-3 py-2 text-sm font-medium outline-none focus:ring-2 focus:ring-redline-500/20 sm:w-auto sm:py-1.5";

// 1 2 3 … 13, with the current page always flanked. Never more than seven controls.
const pageList = (page, pages) => {
  if (pages <= 7) return [...Array(pages).keys()];
  const near = [0, 1, page - 1, page, page + 1, pages - 2, pages - 1]
    .filter((p) => p >= 0 && p < pages);
  const uniq = [...new Set(near)].sort((a, b) => a - b);
  return uniq.flatMap((p, i) => (i && p - uniq[i - 1] > 1 ? ["gap", p] : [p]));
};

const needsLine = (v) => {
  if (v.status === "carried") {
    return `Unfinished — ${v.carried?.count || 0} task(s) moved to ${pretty(v.carried?.to_date)}: “${(v.carried?.reason || "").slice(0, 60)}”`;
  }
  if (v.status === "missed") {
    return v.miss_streak > 1
      ? `Missed ${v.miss_streak} in a row — this is a vendor conversation now`
      : "Nobody showed up — chase the vendor or excuse it";
  }
  if (v.status === "vendor_replied") {
    const last = [...(v.thread || [])].reverse().find((e) => e.side === "vendor" && e.kind !== "system");
    return last ? `${last.name || "The vendor"} replied: “${last.text.slice(0, 80)}”` : "The vendor replied";
  }
  const flagged = (v.thread || []).filter((e) => e.kind === "issue").slice(-1)[0];
  if (flagged) return `Flagged: “${flagged.text.slice(0, 80)}”`;
  if (v.auto_filed) return `Left open — filed with ${v.counts.done} of ${v.counts.total} done, waiting for your review`;
  return `Submitted — ${v.counts.done} of ${v.counts.total} ticked, waiting for your review`;
};

// Phones get three tiles then two, each the same size; `span` is how 5 tiles fill a 6-column grid.
const Stat = ({ k, label, n, active, tone, span, onClick }) => (
  <button onClick={onClick} data-testid={`cl-stat-${k}`}
    className={`${span} px-4 py-3 text-left transition-colors sm:col-span-1 ${active ? "bg-ink text-white" : "bg-white hover:bg-sand-50"}`}>
    <span className={`block font-heading text-2xl font-bold tabular-nums tracking-tight ${active ? "" : tone || ""}`}>{n}</span>
    <span className={`block whitespace-nowrap text-xs font-semibold ${active ? "text-white/70" : "text-muted-foreground"}`}>{label}</span>
  </button>
);

const Row = ({ v, onOpen }) => {
  const st = STATUS_WORDS[v.status] || STATUS_WORDS.scheduled;
  const when = v.auto_filed ? "filed when the window closed"
    : v.submitted_at && v.submitted_at !== "window closed" ? `submitted ${v.submitted_at}`
    : v.window?.end ? `due by ${clock(v.window.end)}` : "";
  const problem = v.notified?.problem;
  return (
    <button onClick={() => onOpen(v.id)} data-testid={`cl-visit-${v.id}`}
      className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-sand-50">
      <span className="min-w-0 flex-1">
        <span className="block font-heading text-[15px] font-bold tracking-tight">{v.name}</span>
        <span className="block text-sm text-muted-foreground line-clamp-2 sm:truncate">
          {v.tech_name ? `${v.tech_name} on site` : "Nobody checked in"}{when ? ` · ${when}` : ""}
        </span>
        {problem && (
          <span data-testid={`cl-visit-notice-${v.id}`}
            className="mt-1 inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700">
            <BellOff className="h-3 w-3" />
            {problem === "failed" ? "The text never reached them" : "They never opened the list"}
          </span>
        )}
      </span>
      <span className="shrink-0 text-right">
        <span className="block text-sm font-semibold tabular-nums">{v.counts.done} of {v.counts.total}</span>
        <span className={`block text-xs font-semibold ${TONE_CLS[st.tone]}`}>{st.label}</span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-sand-400" />
    </button>
  );
};

export function Visits({ tenantId, templates, vendors, config, readOnly, openId, onOpen }) {
  const [data, setData] = useState(null);           // { attention, upcoming, visits, summary, log_total, today }
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(0);
  const [per, setPer] = useState(25);
  const [days, setDays] = useState(30);
  const [status, setStatus] = useState("");
  const [tpl, setTpl] = useState("");
  const [vendor, setVendor] = useState("");
  const [typed, setTyped] = useState("");
  const [q, setQ] = useState("");
  const [showAhead, setShowAhead] = useState(false);
  const [one, setOne] = useState(null);             // a deep-linked visit outside the period
  const logTop = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => { setQ(typed.trim()); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [typed]);

  // Every control that changes what is being looked at resets the page itself. Doing it in an
  // effect instead fired one wasted request against the old page first.
  const onDays = (v) => { setDays(v); setPage(0); };
  const onStatus = (v) => { setStatus(status === v ? "" : v); setPage(0); };
  const onTpl = (v) => { setTpl(v); setPage(0); };
  const onVendor = (v) => { setVendor(v); setPage(0); };
  const onPer = (v) => { setPer(v); setPage(0); };

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data: d } = await api.get(`/tenants/${tenantId}/checklists/visits`, {
        params: { days, status, template_id: tpl, vendor_id: vendor, q, limit: per, skip: page * per },
      });
      setData(d); setRows(d.visits || []);
    } finally { setBusy(false); }
  }, [tenantId, days, status, tpl, vendor, q, per, page]);

  useEffect(() => { load().catch(() => setData({ visits: [], attention: [], upcoming: [], summary: {} })); }, [load]);

  const goto = (p) => {
    setPage(p);
    logTop.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Rows can leave the period while you are reading it; never strand the reader past the end.
  useEffect(() => {
    const last = Math.max(0, Math.ceil((data?.log_total || 0) / per) - 1);
    if (page > last) setPage(last);
  }, [data, per, page]);

  const pool = [...(data?.attention || []), ...(data?.upcoming || []), ...rows, ...(one ? [one] : [])];
  const open = openId ? pool.find((v) => v.id === openId) : null;

  useEffect(() => {
    if (!openId || open) return;
    api.get(`/tenants/${tenantId}/checklists/visits/${openId}`)
      .then((r) => setOne(r.data.visit)).catch(() => onOpen(null));
  }, [openId, open, tenantId, onOpen]);

  if (!data) return <div className="grid h-64 place-items-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  const today = data.today || new Date().toISOString().slice(0, 10);
  if (openId) {
    if (!open) return <div className="grid h-64 place-items-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
    return (
      <VisitRecord tenantId={tenantId} visit={open} readOnly={readOnly} today={today}
        onBack={() => { setOne(null); onOpen(null); }}
        onChange={(u) => {
          setOne((o) => (o && o.id === u.id ? u : o));
          setRows((r) => r.map((v) => (v.id === u.id ? u : v)));
          setData((d) => ({ ...d, attention: (d.attention || []).map((v) => (v.id === u.id ? u : v)),
                            upcoming: (d.upcoming || []).map((v) => (v.id === u.id ? u : v)) }));
          // Patching the row in place kept it in "Needs you" with stale counters — excusing a
          // visit and going back showed it still waiting on you until a manual refresh.
          load().catch(() => {});
        }} />
    );
  }

  const s = data.summary || {};
  const due = s.due || 0;
  const filtered = !!(status || tpl || vendor || q || days !== 30);
  const narrowed = !!(status || tpl || vendor || q);
  const dates = [...new Set(rows.map((v) => v.date))];
  const total = data.log_total || 0;
  const pages = Math.max(1, Math.ceil(total / per));
  const from1 = total ? page * per + 1 : 0;
  const to1 = Math.min(total, page * per + rows.length);
  const periodLabel = (PERIODS.find(([d]) => d === days) || [0, ""])[1];
  const clearAll = () => { setStatus(""); setTpl(""); setVendor(""); setTyped(""); setQ(""); setDays(30); setPage(0); };

  return (
    <div className="space-y-6" data-testid="cl-visits">
      {/* The health of the community, then the filters, then the job — in that order. */}
      <div>
        <p className="mb-2 text-sm text-muted-foreground" data-testid="cl-summary-line">
          <span className="font-semibold text-ink">{periodLabel}</span>
          {data.board_view && <> — {s.filed || 0} visit{(s.filed || 0) === 1 ? "" : "s"} shared with the board.</>}
          {!data.board_view && (narrowed && !due
            ? " — nothing matches these filters."
            : due > 0
              ? <> — {s.filed || 0} of the {due} visits due came back
                  {s.missed ? <>, <span className="font-semibold text-rose-700">{s.missed} missed</span></> : ""}.
                  {!!s.left_open && <>{" "}
                    <button onClick={() => onStatus("left_open")} data-testid="cl-left-open-link"
                      className={`font-semibold underline underline-offset-2 ${status === "left_open" ? "text-ink" : "text-ink-700 hover:text-ink"}`}>
                      {s.left_open} of them {s.left_open === 1 ? "was" : "were"} left open
                    </button>.</>}
                </>
              : " — nothing was scheduled in this period.")}
        </p>
        <div className="grid grid-cols-6 gap-px overflow-hidden rounded-2xl border border-sand-200 bg-sand-200 sm:grid-cols-5">
          <Stat k="due" label="Due" n={due} span="col-span-2" active={status === "due"} onClick={() => onStatus("due")} />
          <Stat k="filed" label="Came back" n={s.filed || 0} span="col-span-2" active={status === "filed"} onClick={() => onStatus("filed")} />
          <Stat k="missed" label="Missed" n={s.missed || 0} span="col-span-2" tone={s.missed ? "text-rose-700" : ""}
            active={status === "missed"} onClick={() => onStatus("missed")} />
          <Stat k="excused" label="Excused" n={s.excused || 0} span="col-span-3" active={status === "excused"} onClick={() => onStatus("excused")} />
          <Stat k="open" label="Not due yet" n={s.open || 0} span="col-span-3" active={status === "open"} onClick={() => onStatus("open")} />
        </div>
      </div>

      <div className="grid grid-cols-2 items-center gap-2 sm:flex sm:flex-wrap">
        <select className={sel} value={days} data-testid="cl-filter-period" onChange={(e) => onDays(Number(e.target.value))}>
          {PERIODS.map(([d, l]) => <option key={d} value={d}>{l}</option>)}
        </select>
        <select className={sel} value={vendor} data-testid="cl-filter-vendor" onChange={(e) => onVendor(e.target.value)}>
          <option value="">Every vendor</option>
          {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select className={`${sel} col-span-2 sm:col-auto`} value={tpl} data-testid="cl-filter-checklist" onChange={(e) => onTpl(e.target.value)}>
          <option value="">Every checklist</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <div className="relative col-span-2 sm:col-auto">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-sand-400" />
          <input value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="cl-filter-search"
            placeholder="Search a checklist or a name" className={`${sel} w-full pl-8 sm:w-60`} />
        </div>
        {filtered && (
          <button data-testid="cl-filter-clear" onClick={clearAll}
            className="inline-flex items-center justify-center gap-1 rounded-full border border-sand-300 bg-white px-3 py-2 text-xs font-semibold hover:border-sand-400 sm:py-1.5">
            <X className="h-3 w-3" /> Clear
          </button>
        )}
      </div>

      {(data.attention || []).length > 0 && (
        <div data-testid="cl-attention">
          <div className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Waiting on you · {data.attention.length}
          </div>
          <div className="divide-y divide-sand-100 overflow-hidden rounded-2xl border border-sand-200 bg-white">
            {data.attention.map((v) => (
              <button key={v.id} onClick={() => onOpen(v.id)} data-testid={`cl-attention-${v.id}`}
                className="flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors hover:bg-sand-50">
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold">{v.name} · {dayHeading(v.date, today).split(" · ")[0]}</span>
                  <span className="block text-sm text-muted-foreground line-clamp-2 sm:truncate">{needsLine(v)}</span>
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-sand-400" />
              </button>
            ))}
          </div>
        </div>
      )}

      {(data.upcoming || []).length > 0 && (
        <div data-testid="cl-upcoming">
          <button onClick={() => setShowAhead((v) => !v)} data-testid="cl-upcoming-toggle"
            className="mb-2 flex w-full flex-wrap items-center gap-x-1.5 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-ink">
            <ChevronRight className={`h-3.5 w-3.5 shrink-0 transition-transform ${showAhead ? "rotate-90" : ""}`} />
            Coming up · {data.upcoming.length}
            <span className="w-full pl-5 font-sans text-[11px] font-medium normal-case tracking-normal text-sand-500 sm:w-auto sm:pl-0">
              built {config?.horizon_days ?? 14} days ahead · open one to excuse it before the day
            </span>
          </button>
          {showAhead && (
            <div className="divide-y divide-sand-100 overflow-hidden rounded-2xl border border-sand-200 bg-white">
              {data.upcoming.map((v) => (
                <button key={v.id} onClick={() => onOpen(v.id)} data-testid={`cl-upcoming-${v.id}`}
                  className="flex w-full items-center gap-3 px-5 py-3 text-left transition-colors hover:bg-sand-50">
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold">{pretty(v.date, { weekday: "short", month: "short" })} · {v.name}</span>
                    <span className="block text-sm text-muted-foreground">
                      {v.counts.total} tasks{v.window?.end ? ` · due by ${clock(v.window.end)}` : ""}
                      {v.status === "excused" ? " · excused" : ""}
                    </span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-sand-400" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div ref={logTop} className="scroll-mt-6 space-y-6">
        {dates.map((d) => (
          <div key={d}>
            <div className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">{dayHeading(d, today)}</div>
            <div className="divide-y divide-sand-100 overflow-hidden rounded-2xl border border-sand-200 bg-white">
              {rows.filter((v) => v.date === d).map((v) => <Row key={v.id} v={v} onOpen={onOpen} />)}
            </div>
          </div>
        ))}

        {total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-sand-200 pt-4" data-testid="cl-pager">
            <p className="text-sm text-muted-foreground" data-testid="cl-pager-count">
              {busy ? "Loading…" : <>Showing <span className="font-semibold text-ink">{from1}–{to1}</span> of {total} visit{total === 1 ? "" : "s"}</>}
            </p>
            <div className="flex items-center gap-1">
              <button onClick={() => goto(page - 1)} disabled={page === 0 || busy} data-testid="cl-page-prev"
                className="inline-flex h-9 items-center gap-1 rounded-full border border-sand-300 bg-white px-3 text-sm font-semibold transition-colors hover:border-sand-400 disabled:opacity-35 disabled:hover:border-sand-300">
                <ChevronLeft className="h-3.5 w-3.5" /> Prev
              </button>
              {pageList(page, pages).map((p, i) => (p === "gap" ? (
                <span key={`gap${i}`} className="px-1 text-sm text-sand-400">…</span>
              ) : (
                <button key={p} onClick={() => goto(p)} disabled={busy} data-testid={`cl-page-${p + 1}`}
                  className={`h-9 min-w-9 rounded-full px-2.5 text-sm font-semibold tabular-nums transition-colors ${
                    p === page ? "bg-ink text-white" : "border border-sand-300 bg-white hover:border-sand-400"}`}>
                  {p + 1}
                </button>
              )))}
              <button onClick={() => goto(page + 1)} disabled={page >= pages - 1 || busy} data-testid="cl-page-next"
                className="inline-flex h-9 items-center gap-1 rounded-full border border-sand-300 bg-white px-3 text-sm font-semibold transition-colors hover:border-sand-400 disabled:opacity-35 disabled:hover:border-sand-300">
                Next <ChevronRight className="h-3.5 w-3.5" />
              </button>
              <select className={`${sel} ml-2`} value={per} data-testid="cl-per-page" onChange={(e) => onPer(Number(e.target.value))}>
                {PER_PAGE.map((n) => <option key={n} value={n}>{n} per page</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      {!rows.length && (
        <p className="rounded-2xl border border-dashed border-sand-300 p-10 text-center text-sm text-muted-foreground" data-testid="cl-empty">
          {narrowed
            ? "Nothing matches that. Widen the period or clear the filters."
            : data.board_view
              ? "Nothing has been shared with the board yet. Your manager decides which visit records the board sees."
              : "No visits in this period yet. Build a checklist and the first one appears on its next scheduled day."}
        </p>
      )}
    </div>
  );
}
